import appService from '@/modules/app/services/appService';
import logError from '@/utils/logError/logError';
import channelsService from '../services/channelsService';

const CORE_INTERNAL_APP_UID = 'CoreInternalApp';

const state = {
  channels: [],
  isFetching: false,
  fetchError: null,
  pendingTogglesByUid: {},
  communicationApps: null,
  isFetchingApps: false,
};

const getters = {
  channels: (s) => s.channels,
  isFetching: (s) => s.isFetching,
  fetchError: (s) => s.fetchError,
  pendingTogglesByUid: (s) => s.pendingTogglesByUid,
  /**
   * Channels owned by comm-gw apps (anything that's not the global
   * CoreInternalApp). Sorted alphabetically by `display_name` (then `app_uid`)
   * so the order in the Channels page is stable across refreshes.
   */
  commGwChannels: (s) => [...s.channels]
    .filter((c) => c?.app_uid && c.app_uid !== CORE_INTERNAL_APP_UID)
    .sort((a, b) => {
      const an = (a.display_name || a.app_uid || '').toLowerCase();
      const bn = (b.display_name || b.app_uid || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    }),
  /** True when a toggle is in flight for a given channel uid. */
  isPendingForChannel: (s) => (uid) => Boolean(s.pendingTogglesByUid[uid]),
  /** Communication apps that are available but not yet installed. */
  availableCommunicationApps: (s) => {
    if (!Array.isArray(s.communicationApps)) return [];
    return s.communicationApps.filter((app) => app.status !== 'installed');
  },
  isFetchingApps: (s) => s.isFetchingApps,
  /**
   * Single card label for a channel. We don't cross-reference Core's
   * `externalApplications` cache here because comm-gw's `channels.app_uid`
   * is currently stored inconsistently (apps.uid for some apps, app_code_name
   * for others, even staff_uid in some legacy test rows) and the
   * externalApplications response doesn't expose `uid` anyway. Falling back
   * to `app_uid` is a last resort just so the card never renders empty.
   */
  cardLabelForChannel: () => (channel) => {
    if (!channel) return '';
    return channel.display_name || channel.app_uid || '';
  },
};

const mutations = {
  setChannels(state, channels) {
    state.channels = Array.isArray(channels) ? channels : [];
  },
  setIsFetching(state, value) {
    state.isFetching = Boolean(value);
  },
  setFetchError(state, error) {
    state.fetchError = error || null;
  },
  setPendingForChannel(state, uid) {
    if (!uid) return;
    state.pendingTogglesByUid = { ...state.pendingTogglesByUid, [uid]: true };
  },
  clearPendingForChannel(state, uid) {
    if (!uid) return;
    const next = { ...state.pendingTogglesByUid };
    delete next[uid];
    state.pendingTogglesByUid = next;
  },
  setCommunicationApps(state, apps) {
    state.communicationApps = Array.isArray(apps) ? apps : [];
  },
  setIsFetchingApps(state, value) {
    state.isFetchingApps = Boolean(value);
  },
  /**
   * Replace a single channel's fields (used for optimistic intercept toggle
   * and again on the server response so the store reflects the truth).
   */
  updateChannelLocally(state, channel) {
    if (!channel?.uid) return;
    state.channels = state.channels.map((c) => (
      c.uid === channel.uid ? { ...c, ...channel } : c
    ));
  },
};

const actions = {
  async fetchChannels({ commit }) {
    commit('setIsFetching', true);
    commit('setFetchError', null);
    try {
      const list = await channelsService.listChannels();
      commit('setChannels', list);
      return list;
    } catch (err) {
      logError('Failed to fetch comm-gw channels for AI Receptionist', err);
      const status = err?.response?.status;
      const message = status === 403
        ? 'web_ai_widget.channels.error_forbidden'
        : err?.message || 'web_ai_widget.channels.error_load';
      commit('setFetchError', message);
      commit('setChannels', []);
      throw err;
    } finally {
      commit('setIsFetching', false);
    }
  },

  /**
   * Optimistic intercept toggle: flips local state immediately so the UI feels
   * instant, then issues PUT /channels/:uid. On failure, reverts and surfaces
   * the error to the caller (so the page can show a toast).
   */
  async setIntercept({ commit, state }, { uid, intercepted }) {
    if (!uid) {
      throw new Error('channel uid is required');
    }
    const before = state.channels.find((c) => c.uid === uid) || null;
    if (!before) {
      throw new Error(`channel [${uid}] not found in store`);
    }
    const desired = Boolean(intercepted);
    commit('updateChannelLocally', { uid, intercepted_by_receptionist: desired });
    commit('setPendingForChannel', uid);
    try {
      const updated = await channelsService.setIntercept(uid, desired);
      if (updated) {
        commit('updateChannelLocally', updated);
      }
      return updated;
    } catch (err) {
      logError('Failed to toggle channel intercept', err, { uid, intercepted: desired });
      commit('updateChannelLocally', before);
      throw err;
    } finally {
      commit('clearPendingForChannel', uid);
    }
  },

  async fetchCommunicationApps({ commit, rootGetters }) {
    commit('setIsFetchingApps', true);
    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      const res = await appService.getAppsByType({ type: 'communication', businessUid });
      const apps = res?.apps || [];
      commit('setCommunicationApps', apps);
      return apps;
    } catch (err) {
      logError('Failed to fetch communication apps', err);
      commit('setCommunicationApps', []);
      return [];
    } finally {
      commit('setIsFetchingApps', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
